import OpenInNewIcon from '@mui/icons-material/OpenInNew';
import {
  Box,
  Button,
  Chip,
  CircularProgress,
  FormControl,
  InputLabel,
  MenuItem,
  Paper,
  Select,
  Stack,
  Tooltip,
  Typography,
  useMediaQuery,
  useTheme,
} from '@mui/material';
import React, { useCallback, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { trackLegislatorPackClick } from './GoogleAnalytics';
import { fetchBillDataFromSheets } from '../services/googleSheets';
import {
  COUNTRIES,
  getStatesForCountry,
  STATE_CENTROIDS,
  CA_PROVINCE_CENTROIDS,
  PL_VOIVODESHIP_CENTROIDS,
  slugToStateName,
} from '../utils/stateUtils';
import { useGeoLocation } from '../contexts/GeoLocationContext';

interface BillStateDirectoryProps {
  billSlug: string;
  billTitle: string;
  country?: string;
}

interface StateRow {
  id: string;
  state: string;
  billName: string;
  legislatorUrl?: string;
  stateSlug: string;
  countrySlug: string;
}

const BillStateDirectory: React.FC<BillStateDirectoryProps> = ({ billSlug, billTitle, country: countryProp }) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const navigate = useNavigate();
  const { setLocation } = useGeoLocation();
  const [rows, setRows] = useState<StateRow[]>([]);
  const [loading, setLoading] = useState(true);
  const [filterStates, setFilterStates] = useState<string[]>([]);
  // Control the open state of the multi-select so we can add a custom action button inside the menu
  const [selectOpen, setSelectOpen] = useState(false);

  // Handler for when user clicks a state tile
  const handleStateClick = useCallback(
    (stateSlug: string, countrySlug: string) => {
      const stateName = slugToStateName(stateSlug);
      const centroid =
        STATE_CENTROIDS[stateName] || CA_PROVINCE_CENTROIDS[stateName] || PL_VOIVODESHIP_CENTROIDS[stateName];
      if (centroid) {
        // Update location context to persist the selected state and country
        setLocation({ lat: centroid.lat, lon: centroid.lon, state: stateName, country: countrySlug });
      }
      // Navigate to country+state-scoped route
      navigate(`/bill/${countrySlug}/${stateSlug}/${billSlug}`);
    },
    [billSlug, navigate, setLocation],
  );

  // Return the raw sheet-provided link (trimmed) without altering protocol so we do not accidentally transform the target (e.g., into an image viewer)
  const rawSheetUrl = (url?: string) => {
    if (!url) return undefined;
    return url.trim();
  };

  useEffect(() => {
    let mounted = true;
    (async () => {
      try {
        const sheetData = await fetchBillDataFromSheets();

        // Debug: Log unique bill titles to see what's in the spreadsheet
        const uniqueTitles = Array.from(new Set(sheetData.map((b: any) => b.billTitle)));
        console.log('BillStateDirectory - Searching for:', billTitle);
        console.log('BillStateDirectory - Available bill titles in sheet:', uniqueTitles);

        const filtered = sheetData.filter((b: any) => {
          const billTitleLower = b.billTitle?.toLowerCase() || '';
          const searchTitleLower = billTitle.toLowerCase();

          // Regular includes matching
          const regularMatch = billTitleLower.includes(searchTitleLower) || searchTitleLower.includes(billTitleLower);

          // Special case for reimbursement bills - match both "alienation" and "alienating behavior" variants
          const isReimbursementBill = billTitleLower.includes('reimbursement') && billTitleLower.includes('parental');
          const isReimbursementSearch =
            searchTitleLower.includes('reimbursement') && searchTitleLower.includes('parental');

          if (isReimbursementBill && isReimbursementSearch) {
            // More flexible matching for reimbursement bills
            const billWords = billTitleLower.split(' ');
            const searchWords = searchTitleLower.split(' ');

            // Check if most key words match
            const keyWords = ['reimbursement', 'fees', 'parental'];
            const matchingKeyWords = keyWords.filter((word) => billWords.includes(word) && searchWords.includes(word));

            return matchingKeyWords.length >= 3; // Match if at least 3 key words match
          }

          // Special case for Child Support Integrity Act - handle typo in spreadsheet ("Integrety" vs "Integrity")
          const isChildSupportIntegrityBill =
            billTitleLower.includes('child support') &&
            (billTitleLower.includes('integrity') || billTitleLower.includes('integrety'));
          const isChildSupportIntegritySearch =
            searchTitleLower.includes('child support') &&
            (searchTitleLower.includes('integrity') || searchTitleLower.includes('integrety'));

          if (isChildSupportIntegrityBill && isChildSupportIntegritySearch) {
            return true;
          }
          if (isChildSupportIntegritySearch && isChildSupportIntegrityBill) {
            return true;
          }

          return regularMatch;
        });

        const mapped: StateRow[] = [];
        for (const country of COUNTRIES) {
          const states = getStatesForCountry(country.slug);
          for (const s of states) {
            const match = filtered.find((f: any) => f.state?.toLowerCase() === s.name.toLowerCase());
            const legislatorUrl = rawSheetUrl(match?.googleDriveUrl);

            mapped.push({
              id: `${country.slug}-${s.slug}`,
              state: s.name,
              billName: match?.billTitle || billTitle,
              legislatorUrl,
              stateSlug: s.slug,
              countrySlug: country.slug,
            });
          }
        }
        if (mounted) setRows(mapped);
      } catch {
        if (mounted) {
          const fallback: StateRow[] = [];
          for (const country of COUNTRIES) {
            const states = getStatesForCountry(country.slug);
            for (const s of states) {
              fallback.push({
                id: `${country.slug}-${s.slug}`,
                state: s.name,
                billName: billTitle,
                stateSlug: s.slug,
                countrySlug: country.slug,
              });
            }
          }
          setRows(fallback);
        }
      } finally {
        if (mounted) setLoading(false);
      }
    })();
    return () => {
      mounted = false;
    };
  }, [billTitle]);

  const displayedRows = filterStates.length > 0 ? rows.filter((r) => filterStates.includes(r.stateSlug)) : rows;

  const handleFilterChange = (event: React.ChangeEvent<{ value: unknown }> | any) => {
    // Filter out any falsy values (e.g., undefined or empty strings) that can arise from action MenuItems
    const value = (event.target.value as string[]).filter(Boolean);
    setFilterStates(value);
  };

  const handleChipDelete = (slug: string) => {
    setFilterStates((prev) => prev.filter((s) => s !== slug));
  };

  if (loading) {
    return (
      <Box sx={{ py: 6, textAlign: 'center' }} aria-live="polite" role="status">
        <CircularProgress sx={{ mb: 2 }} />
        <Typography variant="body1">Loading bill directory...</Typography>
      </Box>
    );
  }

  if (isMobile) {
    return (
      <Box sx={{ py: 4 }}>
        <Typography variant="h5" sx={{ fontWeight: 700, mb: 2 }} aria-label={`Bill directory for ${billTitle}`}>
          Select Your Location
        </Typography>
        <Typography variant="body2" sx={{ mb: 1.5, color: 'text.secondary' }}>
          Browse state-specific bill versions and advocacy materials.
        </Typography>
        <FormControl fullWidth size="small" sx={{ mb: 2 }}>
          <InputLabel id="state-filter-label">Filter by State</InputLabel>
          <Select
            labelId="state-filter-label"
            multiple
            label="Filter by State"
            value={filterStates}
            onChange={handleFilterChange}
            open={selectOpen}
            onOpen={() => setSelectOpen(true)}
            onClose={() => setSelectOpen(false)}
            renderValue={(selected) =>
              (selected as string[])
                .map((slug) => {
                  const match = rows.find((r) => r.stateSlug === slug);
                  return match?.state || slug;
                })
                .join(', ')
            }
          >
            {rows.map((r) => (
              <MenuItem key={r.id} value={r.stateSlug}>
                {r.state}
              </MenuItem>
            ))}
            <MenuItem
              // Custom action button - prevent it from toggling selection
              onClick={(e) => {
                e.stopPropagation();
                setSelectOpen(false);
              }}
              onMouseDown={(e) => {
                // Prevent default selection behavior that can inject an empty value
                e.preventDefault();
              }}
              disableRipple
              sx={(theme) => ({
                mt: 1,
                borderTop: '1px solid',
                borderColor: 'divider',
                justifyContent: 'center',
                fontWeight: 600,
                position: 'sticky',
                bottom: 0,
                zIndex: 1,
                backgroundColor: theme.palette.primary.main,
                color: theme.palette.primary.contrastText,
                borderRadius: 1,
                mx: 1,
                mb: 0.5,
                transition: 'background-color 0.2s ease',
                '&:hover': {
                  backgroundColor: theme.palette.primary.dark,
                },
                '&.Mui-focusVisible': {
                  outline: '2px solid',
                  outlineColor: theme.palette.primary.dark,
                },
              })}
              aria-label={`See results (${(filterStates.length ? rows.filter((r) => filterStates.includes(r.stateSlug)) : rows).length})`}
            >
              See Results (
              {(filterStates.length ? rows.filter((r) => filterStates.includes(r.stateSlug)) : rows).length})
            </MenuItem>
          </Select>
        </FormControl>
        {filterStates.length > 0 && (
          <Stack direction="row" spacing={1} flexWrap="wrap" sx={{ mb: 3 }} aria-label="Selected states">
            <Chip
              key="clear-all"
              label="Remove All"
              color="default"
              variant="outlined"
              onClick={() => setFilterStates([])}
              onDelete={() => setFilterStates([])}
              sx={{ my: 1.5, fontWeight: 500 }}
              aria-label="Remove all selected states"
            />
            {filterStates.map((slug) => {
              const match = rows.find((r) => r.stateSlug === slug);
              return (
                <Chip
                  key={slug}
                  label={match?.state || slug}
                  onDelete={() => handleChipDelete(slug)}
                  sx={{ my: 1.5 }}
                  aria-label={`Remove ${match?.state || slug}`}
                />
              );
            })}
          </Stack>
        )}
        <Box display="flex" flexDirection="column" gap={2}>
          {COUNTRIES.map((c) => {
            const countryRows = displayedRows.filter((r) => r.countrySlug === c.slug);
            if (countryRows.length === 0) return null;
            return (
              <React.Fragment key={c.slug}>
                <Typography variant="h6" sx={{ fontWeight: 700, mt: 2, mb: 1, color: 'text.secondary' }}>
                  {c.name}
                </Typography>
                {countryRows.map((r) => (
                  <Paper key={r.id} elevation={2} sx={{ p: 2, display: 'flex', flexDirection: 'column', gap: 1 }}>
                    <Button
                      onClick={() => handleStateClick(r.stateSlug, r.countrySlug)}
                      variant="text"
                      sx={(theme) => ({
                        textTransform: 'none',
                        justifyContent: 'flex-start',
                        p: 0,
                        fontWeight: 700,
                        fontSize: '1rem',
                        color: theme.palette.mode === 'dark' ? '#FFFFFF' : '#000000',
                        '&:hover': {
                          color: theme.palette.mode === 'dark' ? theme.palette.grey[200] : theme.palette.grey[800],
                        },
                      })}
                      aria-label={`Open ${r.state} version of ${billTitle}`}
                    >
                      {r.state}
                    </Button>
                    <Button
                      variant="outlined"
                      size="small"
                      disabled={!r.legislatorUrl}
                      aria-disabled={!r.legislatorUrl}
                      aria-label={
                        r.legislatorUrl
                          ? `View legislator pack for ${r.state} in new tab`
                          : `Legislator pack coming soon for ${r.state}`
                      }
                      endIcon={r.legislatorUrl ? <OpenInNewIcon fontSize="small" /> : undefined}
                      onClick={() => {
                        if (r.legislatorUrl) {
                          trackLegislatorPackClick(billTitle, r.state);
                          window.open(r.legislatorUrl, '_blank', 'noopener,noreferrer');
                        }
                      }}
                    >
                      View Legislator Pack
                    </Button>
                  </Paper>
                ))}
              </React.Fragment>
            );
          })}
        </Box>
      </Box>
    );
  }

  // Placeholder desktop layout (will upgrade to DataGrid)
  return (
    <Box sx={{ py: 6 }}>
      <Box sx={{ display: 'flex', alignItems: 'center', mb: filterStates.length ? 1 : 2, gap: 2, flexWrap: 'wrap' }}>
        <Typography variant="h5" sx={{ fontWeight: 700, flexGrow: 1, minWidth: 200 }}>
          Browse Bill Versions
        </Typography>
        <FormControl size="small" sx={{ minWidth: 240 }}>
          <InputLabel id="state-filter-label-desktop">Filter by State</InputLabel>
          <Select
            labelId="state-filter-label-desktop"
            multiple
            label="Filter by State"
            value={filterStates}
            onChange={handleFilterChange}
            open={selectOpen}
            onOpen={() => setSelectOpen(true)}
            onClose={() => setSelectOpen(false)}
            renderValue={(selected) =>
              (selected as string[])
                .map((slug) => {
                  const match = rows.find((r) => r.stateSlug === slug);
                  return match?.state || slug;
                })
                .join(', ')
            }
          >
            {rows.map((r) => (
              <MenuItem key={r.id} value={r.stateSlug}>
                {r.state}
              </MenuItem>
            ))}
            <MenuItem
              onClick={(e) => {
                e.stopPropagation();
                setSelectOpen(false);
              }}
              onMouseDown={(e) => {
                e.preventDefault();
              }}
              disableRipple
              sx={(theme) => ({
                mt: 1,
                borderTop: '1px solid',
                borderColor: 'divider',
                justifyContent: 'center',
                fontWeight: 600,
                position: 'sticky',
                bottom: 0,
                zIndex: 1,
                backgroundColor: theme.palette.primary.main,
                color: theme.palette.primary.contrastText,
                borderRadius: 1,
                mx: 1,
                mb: 0.5,
                transition: 'background-color 0.2s ease',
                '&:hover': {
                  backgroundColor: theme.palette.primary.dark,
                },
                '&.Mui-focusVisible': {
                  outline: '2px solid',
                  outlineColor: theme.palette.primary.dark,
                },
              })}
              aria-label={`See results (${(filterStates.length ? rows.filter((r) => filterStates.includes(r.stateSlug)) : rows).length})`}
            >
              See Results (
              {(filterStates.length ? rows.filter((r) => filterStates.includes(r.stateSlug)) : rows).length})
            </MenuItem>
          </Select>
        </FormControl>
      </Box>
      {filterStates.length > 0 && (
        <Stack direction="row" spacing={1} flexWrap="wrap" sx={{ mb: 2 }} aria-label="Selected states">
          <Chip
            key="clear-all"
            label="Remove All"
            color="default"
            variant="outlined"
            onClick={() => setFilterStates([])}
            onDelete={() => setFilterStates([])}
            sx={{ my: 0.75, fontWeight: 500 }}
            aria-label="Remove all selected states"
          />
          {filterStates.map((slug) => {
            const match = rows.find((r) => r.stateSlug === slug);
            return (
              <Chip
                key={slug}
                label={match?.state || slug}
                onDelete={() => handleChipDelete(slug)}
                sx={{ my: 0.75 }}
                aria-label={`Remove ${match?.state || slug}`}
              />
            );
          })}
        </Stack>
      )}
      {COUNTRIES.map((c) => {
        const countryRows = displayedRows.filter((r) => r.countrySlug === c.slug);
        if (countryRows.length === 0) return null;
        return (
          <React.Fragment key={c.slug}>
            <Typography variant="h5" sx={{ fontWeight: 700, mt: 3, mb: 2 }}>
              {c.name}
            </Typography>
            <Box
              component="ul"
              sx={{
                listStyle: 'none',
                m: 0,
                p: 0,
                display: 'grid',
                gridTemplateColumns: 'repeat(auto-fill,minmax(220px,1fr))',
                gap: 2,
              }}
            >
              {countryRows.map((r) => (
                <Paper key={r.id} component="li" sx={{ p: 2, display: 'flex', flexDirection: 'column', gap: 1 }}>
                  <Button
                    onClick={() => handleStateClick(r.stateSlug, r.countrySlug)}
                    variant="text"
                    sx={(theme) => ({
                      textTransform: 'none',
                      justifyContent: 'flex-start',
                      p: 0,
                      fontWeight: 700,
                      fontSize: '1rem',
                      color: theme.palette.mode === 'dark' ? '#FFFFFF' : '#000000',
                      '&:hover': {
                        color: theme.palette.mode === 'dark' ? theme.palette.grey[200] : theme.palette.grey[800],
                      },
                    })}
                    aria-label={`Open ${r.state} version of ${billTitle}`}
                  >
                    {r.state}
                  </Button>
                  <Tooltip title={r.legislatorUrl ? 'Open Legislator Pack' : 'Coming soon'} arrow>
                    <span>
                      <Button
                        variant="outlined"
                        size="small"
                        disabled={!r.legislatorUrl}
                        aria-disabled={!r.legislatorUrl}
                        aria-label={
                          r.legislatorUrl
                            ? `View legislator pack for ${r.state} in new tab`
                            : `Legislator pack coming soon for ${r.state}`
                        }
                        endIcon={r.legislatorUrl ? <OpenInNewIcon fontSize="small" /> : undefined}
                        onClick={() => {
                          if (r.legislatorUrl) {
                            trackLegislatorPackClick(billTitle, r.state);
                            window.open(r.legislatorUrl, '_blank', 'noopener,noreferrer');
                          }
                        }}
                      >
                        View Legislator Pack
                      </Button>
                    </span>
                  </Tooltip>
                </Paper>
              ))}
            </Box>
          </React.Fragment>
        );
      })}
    </Box>
  );
};

export default BillStateDirectory;
